"use client";

import * as React from "react";
import { cn } from "@baluu/ui/lib/utils";
import { SessionCard } from "@baluu/ui/components/checkout/session-card";
import { CalloutBox } from "@baluu/ui/components/checkout/callout-box";
import { CheckoutSteps } from "@baluu/ui/components/checkout/checkout-steps";
import { Loader2, Calendar as CalendarIcon, X, Info, Eye, EyeOff, MapPin } from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@baluu/ui/components/popover";
import { Calendar } from "@baluu/ui/components/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@baluu/ui/components/select";
import { useCheckoutPanel } from "@baluu/ui/components/checkout/checkout-panel";
import {
  resolveViewerTimezone,
  shouldShowTimezoneLabel,
} from "./utils/format";

type TermDate = {
  startDateTime: string;
  duration?: number;
};

type Session = {
  id: string;
  date?: string;
  // Optional custom title for this session
  title?: string;
  // Optional per-session content (e.g. the painting for this Paint & Sip date)
  description?: string | null;
  image?: { url: string; alt?: string } | null;
  startDateTime: string;
  duration?: number;
  location?: string;
  price?: number;
  availability: number;
  isOnline?: boolean;
  isTerm?: boolean;
  eventId?: string; // For term sessions: the term's event id (opens the term popup)
  termType?: "fullTerm" | "proRata"; // For term sessions: full term or joining mid-term
  tutorId?: string; // ID to look up in tutors array
  tutorName?: string; // Direct tutor name if available
  tutorImage?: string; // Direct tutor image if available
  termName?: string;
  additionalClasses?: TermDate[];
  disabled?: boolean;
};

type SessionsByMonth = {
  month: string;
  year: number;
  sessions: Session[];
};

type Tutor = {
  id: string;
  name: string;
  fullName?: string;
  image?: string;
  imageUrl?: string;
};

// Schools that should not show tutor names in session cards
const SCHOOLS_HIDE_TUTOR_NAME = ["bmDxPjPqe7QoNLk76", "3t7gZeu8pz7AiAeAM"];

type DateSelectionStepProps = {
  sessions: Session[];
  selectedSessionId?: string;
  onSelectSession: (session: Session) => void;
  isLoading?: boolean;
  hasMoreDates?: boolean;
  onLoadMore?: () => void;
  onJoinWaitlist?: (sessionId?: string, eventId?: string) => void;
  currency?: string;
  timezone?: string;
  className?: string;
  tutors?: Tutor[]; // Tutors array to look up by session.tutorId
  schoolId?: string; // School ID for conditional display logic
  hidePrice?: boolean; // Hide prices (for bundle bookings)
  isFromPrice?: boolean; // Show "from" prefix for prices (when course.isPriceFrom is enabled)
  hideDisplayAvailableTickets?: boolean; // Hide exact ticket count, show generic availability
  headerTitle?: string; // Custom header title (default: "Choose a Date")
  headerDescription?: string; // Custom header description
  /** Multi-select mode: allow selecting multiple sessions */
  isMultiSelect?: boolean;
  /** IDs of currently selected sessions (multi-select mode) */
  selectedSessionIds?: string[];
  /** Toggle session selection (multi-select mode) */
  onToggleSession?: (session: Session) => void;
  /** Maximum number of selectable dates (multi-select mode) */
  maxDateSelections?: number;
  /** Active "from date" filter (null = no filter) */
  fromDate?: Date | null;
  /** Called when the user changes or clears the from-date filter */
  onChangeFromDate?: (date: Date | null) => void;
  /** Whether to hide sold-out sessions from the list */
  hideSoldOut?: boolean;
  /** Called when the user toggles the hide-sold-out filter */
  onChangeHideSoldOut?: (value: boolean) => void;
  /** Distinct locations of this listing's sessions — a filter dropdown is shown when there are 2+ */
  locationOptions?: { id: string; name: string }[];
  /** Active location filter (PartnerLocation id; null/undefined = all locations) */
  locationFilter?: string | null;
  /** Called when the user changes or clears the location filter */
  onChangeLocationFilter?: (locationId: string | null) => void;
  /**
   * Per-session quantity the current customer has already added to their
   * basket. Subtracted from `session.availability` when rendering "spots
   * left" so the customer can't add the same single-seat session twice.
   * The session card shows the user's own basket as eaten capacity — other
   * users' basket contents are not reflected here (that race is caught at
   * the checkout step). Keyed by session id; absent keys mean 0.
   */
  reservedSessionQuantities?: Record<string, number>;
};

function getOrdinalSuffix(day: number): string {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

/**
 * Format date in the appropriate timezone based on session type:
 * - OFFLINE sessions: Use the course/session timezone
 * - ONLINE sessions: Use the user's local timezone
 */
function formatDateInTimezone(
  dateString: string,
  timezone?: string,
  isOnline?: boolean,
): string {
  // Determine which timezone to use
  const displayTimezone =
    isOnline && typeof window !== "undefined"
      ? Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || timezone
      : timezone;

  // Use Intl.DateTimeFormat with the appropriate timezone
  const options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    day: "numeric",
    month: "long",
    timeZone: displayTimezone,
  };

  try {
    const date = new Date(dateString);
    const formatted = new Intl.DateTimeFormat("en-GB", options).format(date);
    // Add ordinal suffix
    const day = new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      timeZone: displayTimezone,
    });
    const dayNum = parseInt(day, 10);
    // Replace the day number with ordinal version
    return formatted.replace(
      new RegExp(`\\b${dayNum}\\b`),
      `${dayNum}${getOrdinalSuffix(dayNum)}`,
    );
  } catch {
    // Fallback to basic formatting
    const date = new Date(dateString);
    const weekday = date.toLocaleDateString("en-GB", { weekday: "short" });
    const day = date.getDate();
    const month = date.toLocaleDateString("en-GB", { month: "long" });
    return `${weekday} ${day}${getOrdinalSuffix(day)} ${month}`;
  }
}

/**
 * Format time in the appropriate timezone based on session type:
 * - OFFLINE sessions: Use the course/session timezone
 * - ONLINE sessions: Use the user's local timezone
 */
function formatTimeInTimezone(
  dateString: string,
  duration: number | undefined,
  timezone?: string,
  isOnline?: boolean,
): string {
  // Determine which timezone to use
  const displayTimezone =
    isOnline && typeof window !== "undefined"
      ? Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || timezone
      : timezone;

  const options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: displayTimezone,
  };

  try {
    const date = new Date(dateString);
    const startTime = new Intl.DateTimeFormat("en-GB", options)
      .format(date)
      .toLowerCase()
      .replace(" ", "");

    if (duration) {
      const endDate = new Date(date.getTime() + duration * 60000);
      const endTime = new Intl.DateTimeFormat("en-GB", options)
        .format(endDate)
        .toLowerCase()
        .replace(" ", "");
      return `${startTime} - ${endTime}`;
    }

    return startTime;
  } catch {
    // Fallback to basic formatting
    const date = new Date(dateString);
    const startTime = date
      .toLocaleTimeString("en-GB", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase()
      .replace(" ", "");

    if (duration) {
      const endDate = new Date(date.getTime() + duration * 60000);
      const endTime = endDate
        .toLocaleTimeString("en-GB", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
        .toLowerCase()
        .replace(" ", "");
      return `${startTime} - ${endTime}`;
    }

    return startTime;
  }
}

/**
 * Get the timezone label to display.
 * Returns the full IANA identifier (e.g., "Europe/London", "America/New_York") for clarity.
 * For online sessions: show user's local timezone (times are converted to it)
 * For offline sessions: show course timezone
 *
 * Either way the label only renders when the visitor's clock differs from the
 * school's — see shouldShowTimezoneLabel. Naming the timezone a visitor is
 * already in reads as a venue to them, which is the confusion this
 * suppression exists to avoid.
 */
function getTimezoneLabel(
  timezone?: string,
  isOnline?: boolean,
  dateString?: string,
): string | undefined {
  if (!timezone) return undefined;

  const userTimezone = resolveViewerTimezone();

  // Determine which timezone we're displaying in
  const displayTimezone = isOnline ? userTimezone || timezone : timezone;

  // Always compare the SCHOOL's zone against the visitor's — never
  // displayTimezone, which for online sessions IS the visitor's zone and
  // would make this false unconditionally. For online sessions the label is
  // the viewer's own zone, shown exactly when the times were converted to it.
  if (!shouldShowTimezoneLabel(timezone, dateString, userTimezone)) {
    return undefined;
  }

  return displayTimezone;
}

function formatPrice(
  price: number | undefined | null,
  currency: string = "GBP",
): string | undefined {
  if (price === undefined || price === null || isNaN(price)) return undefined;

  const symbols: Record<string, string> = {
    GBP: "£",
    USD: "$",
    EUR: "€",
    AUD: "A$",
    CAD: "C$",
    NZD: "NZ$",
    CHF: "CHF",
    SEK: "kr",
    NOK: "kr",
    DKK: "kr",
    INR: "₹",
    JPY: "¥",
    CNY: "¥",
  };
  const symbol = symbols[currency] || currency + " ";

  // Price is in minor units (cents/pence), divide by 100
  const priceValue = price / 100;

  // Format without decimal places if whole number
  if (priceValue % 1 === 0) {
    return `${symbol}${priceValue}`;
  }
  return `${symbol}${priceValue.toFixed(2)}`;
}

function groupSessionsByMonth(
  sessions: Session[],
  timezone?: string,
): SessionsByMonth[] {
  const groups: Record<string, SessionsByMonth> = {};

  sessions.forEach((session) => {
    // Use proper timezone for grouping
    const displayTimezone =
      session.isOnline && typeof window !== "undefined"
        ? Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || timezone
        : timezone;

    const date = new Date(session.startDateTime);
    let month: string;
    let year: number;

    try {
      month = new Intl.DateTimeFormat("en-GB", {
        month: "long",
        timeZone: displayTimezone,
      }).format(date);
      year = parseInt(
        new Intl.DateTimeFormat("en-GB", {
          year: "numeric",
          timeZone: displayTimezone,
        }).format(date),
        10,
      );
    } catch {
      month = date.toLocaleDateString("en-GB", { month: "long" });
      year = date.getFullYear();
    }

    const key = `${month}-${year}`;

    if (!groups[key]) {
      groups[key] = { month, year, sessions: [] };
    }
    groups[key].sessions.push(session);
  });

  // Sort month groups by date (earliest first)
  const sortedGroups = Object.values(groups).sort((a, b) => {
    const dateA = new Date(`${a.month} 1, ${a.year}`);
    const dateB = new Date(`${b.month} 1, ${b.year}`);
    return dateA.getTime() - dateB.getTime();
  });

  // Sort sessions within each month group by date (earliest first)
  sortedGroups.forEach((group) => {
    group.sessions.sort((a, b) => {
      const dateA = new Date(a.startDateTime);
      const dateB = new Date(b.startDateTime);
      return dateA.getTime() - dateB.getTime();
    });
  });

  return sortedGroups;
}

function DateSelectionStep({
  sessions,
  selectedSessionId,
  onSelectSession,
  isLoading = false,
  hasMoreDates = false,
  onLoadMore,
  onJoinWaitlist,
  currency = "GBP",
  timezone,
  className,
  tutors = [],
  schoolId,
  hidePrice = false,
  isFromPrice = false,
  hideDisplayAvailableTickets = false,
  headerTitle,
  headerDescription,
  isMultiSelect = false,
  selectedSessionIds = [],
  onToggleSession,
  maxDateSelections = 10,
  fromDate,
  onChangeFromDate,
  hideSoldOut = false,
  onChangeHideSoldOut,
  locationOptions,
  locationFilter,
  onChangeLocationFilter,
  reservedSessionQuantities,
}: DateSelectionStepProps) {
  const effectiveAvailabilityFor = React.useCallback(
    (session: Session): number => {
      const held = reservedSessionQuantities?.[session.id] ?? 0;
      return Math.max(0, (session.availability ?? 0) - held);
    },
    [reservedSessionQuantities],
  );
  const visibleSessions = hideSoldOut
    ? sessions.filter((s) => effectiveAvailabilityFor(s) > 0)
    : sessions;
  const groupedSessions = groupSessionsByMonth(visibleSessions, timezone);
  const hiddenSoldOutCount = sessions.length - visibleSessions.length;

  // When the panel renders a sticky header, offset the toolbar to clear it.
  // headerHeight is published by CheckoutPanel.Header via a ResizeObserver and
  // is 0 when no sticky header is rendered (embed mode), so top: 0 is correct.
  const { headerHeight } = useCheckoutPanel();

  const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);

  const hasLocationFilterUI =
    !!onChangeLocationFilter && (locationOptions?.length ?? 0) >= 2;

  const formatChipDate = (date: Date): string => {
    try {
      return new Intl.DateTimeFormat("en-GB", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: timezone,
      }).format(date);
    } catch {
      return date.toDateString();
    }
  };

  // Check if tutor name should be hidden for this school
  const shouldHideTutorName =
    schoolId && SCHOOLS_HIDE_TUTOR_NAME.includes(schoolId);

  // Look up tutor by ID or use direct values
  const getTutorInfo = (
    session: Session,
  ): { name?: string; image?: string } => {
    // Hide tutor info for specific schools
    if (shouldHideTutorName) {
      return { name: undefined, image: undefined };
    }

    // First check if session has direct tutor info
    if (session.tutorName || session.tutorImage) {
      return {
        name: session.tutorName,
        image: session.tutorImage,
      };
    }

    // Look up by tutorId in tutors array
    if (session.tutorId && tutors.length > 0) {
      const tutor = tutors.find(
        (t) => t.id === session.tutorId || (t as any)._id === session.tutorId,
      );
      if (tutor) {
        return {
          name: tutor.fullName || tutor.name,
          image:
            tutor.imageUrl ||
            tutor.image ||
            (tutor as any).profile?.imageUrl ||
            (tutor as any).profile?.image?.url,
        };
      }
    }

    return {};
  };

  const multiSelectCount = selectedSessionIds.length;
  const isAtDateLimit = isMultiSelect && multiSelectCount >= maxDateSelections;

  return (
    <div className={cn("space-y-6", className)}>
      <CheckoutSteps.Header
        title={headerTitle || (isMultiSelect ? "Choose your Dates" : "Choose a Date")}
        description={
          headerDescription ||
          (isMultiSelect
            ? "Select the dates you wish to attend. You can pick multiple dates."
            : "Choose a date you wish to attend this event.")
        }
      />

      {/* Multi-select inline hint (shown until first date is selected) */}
      {isMultiSelect && multiSelectCount === 0 && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Info className="w-3.5 h-3.5 shrink-0" />
          <span>Tap each date you'd like to attend — you can book multiple at once.</span>
        </div>
      )}

      {/* Multi-select count badge */}
      {isMultiSelect && multiSelectCount > 0 && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-emeraldish/10 border border-emeraldish/20 rounded-lg">
          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-emeraldish text-white text-xs font-bold">
            {multiSelectCount}
          </div>
          <span className="text-sm font-medium text-gray-700">
            {multiSelectCount} {multiSelectCount === 1 ? "date" : "dates"} selected
            {isAtDateLimit && (
              <span className="text-xs text-muted-foreground ml-1">
                (maximum reached)
              </span>
            )}
          </span>
        </div>
      )}

      {/* Date filter toolbar */}
      {(onChangeFromDate || onChangeHideSoldOut || hasLocationFilterUI) && (
        <div
          style={{ top: headerHeight }}
          className={cn(
            "sticky z-30 flex flex-wrap items-center gap-2 py-3",
            "bg-background/95 supports-[backdrop-filter]:bg-background/80 backdrop-blur",
          )}
        >
          {onChangeFromDate && (
            <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="secondary" size="sm" className="gap-2">
                  <CalendarIcon className="w-4 h-4" />
                  {fromDate ? "Change date" : "Jump to date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0 z-[10002]"
                align="start"
                container={null}
              >
                <Calendar
                  mode="single"
                  selected={fromDate ?? undefined}
                  onSelect={(date) => {
                    if (date) {
                      onChangeFromDate(date);
                      setIsCalendarOpen(false);
                    }
                  }}
                  captionLayout="dropdown"
                  startMonth={new Date()}
                  endMonth={
                    new Date(
                      new Date().setFullYear(new Date().getFullYear() + 2),
                    )
                  }
                  disabled={{ before: new Date() }}
                />
              </PopoverContent>
            </Popover>
          )}

          {onChangeHideSoldOut && (
            <Button
              variant={hideSoldOut ? "default" : "secondary"}
              size="sm"
              className="gap-2"
              onClick={() => onChangeHideSoldOut(!hideSoldOut)}
              aria-pressed={hideSoldOut}
            >
              {hideSoldOut ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
              Hide sold out
            </Button>
          )}

          {onChangeLocationFilter &&
            locationOptions &&
            locationOptions.length >= 2 && (
              <Select
                value={locationFilter ?? "all"}
                onValueChange={(value) =>
                  onChangeLocationFilter(value === "all" ? null : value)
                }
              >
                {/* Styled to sit alongside the secondary pill buttons */}
                <SelectTrigger className="h-8 w-auto gap-2 rounded-md border-0 bg-secondary text-secondary-foreground text-sm font-medium shadow-none hover:bg-secondary/80">
                  <MapPin className="w-4 h-4" />
                  <SelectValue placeholder="All locations" />
                </SelectTrigger>
                {/* container={null} + z-[10002]: renders inline so the dropdown
                    works inside the checkout overlay AND the webcomponents
                    Shadow DOM — matches participants-step. */}
                <SelectContent container={null} className="z-[10002]">
                  <SelectItem value="all">All locations</SelectItem>
                  {locationOptions.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

          {fromDate && onChangeFromDate && (
            <div className="inline-flex items-center gap-1.5 pl-3 pr-1 py-1 rounded-full bg-muted text-sm">
              <span className="text-muted-foreground">From</span>
              <span className="font-medium">{formatChipDate(fromDate)}</span>
              <button
                type="button"
                onClick={() => onChangeFromDate(null)}
                className="ml-1 p-0.5 rounded-full hover:bg-background/60"
                aria-label="Clear date filter"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {locationFilter && onChangeLocationFilter && (
            <div className="inline-flex items-center gap-1.5 pl-3 pr-1 py-1 rounded-full bg-muted text-sm">
              <span className="text-muted-foreground">At</span>
              <span className="font-medium">
                {locationOptions?.find((o) => o.id === locationFilter)?.name ??
                  "Location"}
              </span>
              <button
                type="button"
                onClick={() => onChangeLocationFilter(null)}
                className="ml-1 p-0.5 rounded-full hover:bg-background/60"
                aria-label="Clear location filter"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {isLoading && sessions.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground/70" />
        </div>
      )}

      {/* Sessions List */}
      {groupedSessions.map((group) => (
        <SessionCard.Group
          key={`${group.month}-${group.year}`}
          title={`${group.month.toUpperCase()} ${group.year}`}
        >
          {group.sessions.map((session) => {
            // Build term dates array (first date + additional classes)
            const termDates: { startDateTime: string; duration?: number }[] =
              session.isTerm || session.additionalClasses?.length
                ? [
                    {
                      startDateTime: session.startDateTime,
                      duration: session.duration,
                    },
                    ...(session.additionalClasses || []),
                  ]
                : [];

            const tutorInfo = getTutorInfo(session);
            // Pass the session's startDateTime to get correct DST-aware timezone abbreviation (e.g., BST vs GMT)
            const timezoneLabel = getTimezoneLabel(
              timezone,
              session.isOnline,
              session.startDateTime,
            );

            // Format price with "from" prefix if isFromPrice is enabled
            const formattedPrice = hidePrice
              ? undefined
              : isFromPrice && session.price !== undefined
                ? `from ${formatPrice(session.price, currency)}`
                : formatPrice(session.price, currency);

            const effectiveSpots = effectiveAvailabilityFor(session);
            const alreadyInBasket =
              (reservedSessionQuantities?.[session.id] ?? 0) > 0;
            return (
              <SessionCard
                key={session.id}
                title={
                  session.termName ||
                  formatDateInTimezone(
                    session.startDateTime,
                    timezone,
                    session.isOnline,
                  )
                }
                sessionTitle={session.title}
                sessionDescription={session.description ?? undefined}
                sessionImage={session.image?.url}
                sessionImageAlt={session.image?.alt}
                time={formatTimeInTimezone(
                  session.startDateTime,
                  session.duration,
                  timezone,
                  session.isOnline,
                )}
                timezoneLabel={timezoneLabel}
                location={session.isOnline ? "Online" : session.location}
                price={formattedPrice}
                spotsLeft={effectiveSpots}
                hideDisplayAvailableTickets={hideDisplayAvailableTickets}
                isTerm={
                  session.isTerm || (session.additionalClasses?.length ?? 0) > 0
                }
                termType={session.termType}
                termDates={termDates}
                timezone={timezone}
                isOnline={session.isOnline}
                selected={
                  isMultiSelect
                    ? selectedSessionIds.includes(session.id)
                    : session.id === selectedSessionId
                }
                disabled={
                  session.disabled ||
                  effectiveSpots <= 0 ||
                  (isAtDateLimit && !selectedSessionIds.includes(session.id))
                }
                onClick={() =>
                  isMultiSelect && onToggleSession
                    ? onToggleSession(session)
                    : onSelectSession(session)
                }
                tutorName={tutorInfo.name}
                tutorImage={tutorInfo.image}
                // Show waitlist only when REAL availability is zero (i.e. the
                // session is genuinely sold out at the source) — not when
                // it's "out of seats" purely because *this* user has eaten
                // the remaining capacity in their own basket. In that case
                // the right CTA is "remove from basket", not "join waitlist".
                showWaitlist={
                  session.availability <= 0 &&
                  !alreadyInBasket &&
                  !!onJoinWaitlist
                }
                onJoinWaitlist={
                  onJoinWaitlist
                    ? () =>
                        // Term cards carry an eventId → open the term popup;
                        // regular sessions pass their own sessionId.
                        onJoinWaitlist(
                          session.eventId ? undefined : session.id,
                          session.eventId,
                        )
                    : undefined
                }
              />
            );
          })}
        </SessionCard.Group>
      ))}

      {/* Load More Button */}
      {hasMoreDates && onLoadMore && (
        <button
          onClick={onLoadMore}
          disabled={isLoading}
          className={cn(
            "w-full py-3 text-center font-medium rounded-lg border border-border",
            "hover:bg-muted transition-colors",
            isLoading && "opacity-50 cursor-not-allowed",
          )}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading...
            </span>
          ) : (
            "See more dates"
          )}
        </button>
      )}

      {/* Waitlist Callout - Always show when sessions exist */}
      {sessions.length > 0 && onJoinWaitlist && (
        <CalloutBox
          title="Can't make these dates?"
          description="You don't need to miss out! We can let you know when new dates become available."
          buttonText="Join New Date Waiting List"
          onButtonClick={() => onJoinWaitlist()}
          variant="primary"
        />
      )}

      {/* Empty State */}
      {!isLoading && sessions.length === 0 && (
        <div className="text-center py-12 bg-card rounded-lg border border-border">
          <p className="text-muted-foreground">
            {locationFilter
              ? "No dates match the current filters."
              : fromDate
                ? `No dates available from ${formatChipDate(fromDate)}. Try a different date.`
                : "No dates available at the moment."}
          </p>
          {locationFilter && onChangeLocationFilter ? (
            <button
              onClick={() => {
                onChangeLocationFilter(null);
                if (fromDate && onChangeFromDate) onChangeFromDate(null);
              }}
              className="mt-4 text-primary hover:text-primary-hover font-medium"
            >
              Clear filters
            </button>
          ) : (
            fromDate &&
            onChangeFromDate && (
              <button
                onClick={() => onChangeFromDate(null)}
                className="mt-4 text-primary hover:text-primary-hover font-medium"
              >
                Clear filter
              </button>
            )
          )}
          {!fromDate && !locationFilter && onJoinWaitlist && (
            <button
              onClick={() => onJoinWaitlist()}
              className="mt-4 text-primary hover:text-primary-hover font-medium"
            >
              Join the waiting list
            </button>
          )}
        </div>
      )}

      {/* All-sold-out filtered state */}
      {!isLoading &&
        sessions.length > 0 &&
        visibleSessions.length === 0 &&
        hideSoldOut &&
        onChangeHideSoldOut && (
          <div className="text-center py-12 bg-card rounded-lg border border-border">
            <p className="text-muted-foreground">
              {hiddenSoldOutCount === 1
                ? "The only available date is sold out."
                : `All ${hiddenSoldOutCount} dates are sold out.`}
            </p>
            <button
              onClick={() => onChangeHideSoldOut(false)}
              className="mt-4 text-primary hover:text-primary-hover font-medium"
            >
              Show sold out dates
            </button>
          </div>
        )}
    </div>
  );
}

export { DateSelectionStep };
export type { Session, DateSelectionStepProps };
